import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { trainingsService, CreateTrainingData } from '../../services/trainings.service';
import { Crown, AlertCircle, CheckCircle } from 'lucide-react';
import categoriesService, { Category, Subcategory, Field } from '../../services/categories.service';
import countriesService, { Country, Wilaya } from '../../services/countries.service';
import { levelsService } from '../../services/levels.service';
import type { Level } from '../../services/levels.service';
import { authService } from '../../services/auth.service';
import { useLocalizedName } from '../../utils/localization';
import { useTranslation } from 'react-i18next';

const CreateTrainingPage: React.FC = () => {
  const navigate = useNavigate();
  const { getLocalizedName } = useLocalizedName();
  const { t } = useTranslation();
  const [formData, setFormData] = useState<CreateTrainingData>({
    title: '',
    banner_image: '',
    country_id: undefined,
    city: '',
    wilaya_id: undefined,
    reference: '',
    domaine_id: 0, // Will be validated to ensure it's not 0
    subcategory_id: '',
    format: '',
    level: '',
    duration: '',
    schedule_date: '',   // optional – filled only if user wants to auto-create a session
    full_description: '',
    learning_objectives: '',
    skills_gained: '',
    target_audience: '',
    detailed_program_pdf: '',
    trainer_profile_snippets: '',
    schedule_dates: '',
    pricing_dzd: 0, // Default to 0 for free training
    trainer: '',
    language: '',
  });
  const [durationValue, setDurationValue] = useState<number>(0);
  const [durationUnit, setDurationUnit] = useState<string>('heures');
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [fileErrors, setFileErrors] = useState<Record<string, string>>({});
  const [canCreate, setCanCreate] = useState<{ canCreate: boolean; message?: string } | null>(null);
  const [checkingPermission, setCheckingPermission] = useState(true);

  // Hierarchical selection states
  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [availableFields, setAvailableFields] = useState<Field[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingSubcategories, setLoadingSubcategories] = useState(false);
  const [loadingFields, setLoadingFields] = useState(false);

  // Countries and wilayas states
  const [countries, setCountries] = useState<Country[]>([]);
  const [wilayas, setWilayas] = useState<Wilaya[]>([]);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [loadingWilayas, setLoadingWilayas] = useState(false);

  // Levels states
  const [levels, setLevels] = useState<Level[]>([]);
  const [loadingLevels, setLoadingLevels] = useState(true);

  useEffect(() => {
    checkCreatePermission();
    loadData();
  }, []);

  // Debug effect to log state changes
  useEffect(() => {
  }, [countries]);

  useEffect(() => {
  }, [wilayas]);

  useEffect(() => {
  }, [formData.country_id]);

  // Load subcategories based on training center's category and countries
  const loadData = async () => {
    try {
      setLoadingSubcategories(true);
      setLoadingCountries(true);
      setLoadingLevels(true);

      // Load countries, levels and get training center info in parallel
      const [countriesData, levelsData, trainingCenterInfo] = await Promise.all([
        countriesService.getCountries(),
        levelsService.getAllLevels(),
        authService.getTrainingCenterInfo()
      ]);


      setCountries(countriesData);
      setLevels(levelsData);

      if (trainingCenterInfo.category_id) {
        try {
          // Load subcategories for this center's category using categoriesService (same as admin)
          const subcategoriesData = await categoriesService.getSubcategoriesByCategory(trainingCenterInfo.category_id);
          setSubcategories(subcategoriesData);
        } catch (subError) {
          // Try to load all subcategories as fallback
          try {
            const allSubcategories = await categoriesService.getSubcategories();
            setSubcategories(allSubcategories);
          } catch (fallbackError) {
          }
        }
      } else {
        // Try to load all subcategories as fallback
        try {
          const allSubcategories = await categoriesService.getSubcategories();
          setSubcategories(allSubcategories);
        } catch (fallbackError) {
        }
      }
    } catch (error) {
    } finally {
      setLoadingSubcategories(false);
      setLoadingCountries(false);
      setLoadingLevels(false);
    }
  };

  // Load fields when subcategory changes
  useEffect(() => {
    const loadFields = async () => {
      if (formData.subcategory_id) {
        try {
          setLoadingFields(true);
          const fieldsData = await categoriesService.getFieldsBySubcategory(formData.subcategory_id);
          setAvailableFields(fieldsData);
        } catch (error) {
        } finally {
          setLoadingFields(false);
        }
      } else {
        setAvailableFields([]);
      }
    };

    loadFields();
  }, [formData.subcategory_id]);

  const checkCreatePermission = async () => {
    try {
      setCheckingPermission(true);
      const result = await trainingsService.canCreateTraining();
      setCanCreate(result);
    } catch (error) {
      setCanCreate({ canCreate: false, message: t('trainingCenter.createTraining.errors.checkingPermission') });
    } finally {
      setCheckingPermission(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    // Handle number inputs properly
    let processedValue: any = value;
    if (name === 'pricing_dzd') {
      // Convert to number, use 0 if empty or invalid
      const numValue = parseFloat(value);
      processedValue = isNaN(numValue) ? 0 : numValue;
    } else if (name === 'domaine_id') {
      // Convert to number, use 0 if empty or invalid
      const numValue = parseInt(value);
      processedValue = isNaN(numValue) ? 0 : numValue;
    } else if (name === 'country_id') {
      // Convert to number, use undefined if empty or invalid
      const numValue = parseInt(value);
      processedValue = isNaN(numValue) ? undefined : numValue;
    }

    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };



  // Handle country change and load wilayas if Algeria is selected
  const handleCountryChange = async (countryId: number) => {
    // Find the country name to check if it's Algeria
    const selectedCountry = countries.find(c => c.id === countryId);
    const countryName = selectedCountry?.name || '';


    setFormData(prev => ({
      ...prev,
      country_id: countryId,
      city: '',
      wilaya_id: undefined
    }));

    // Check if it's Algeria by name or by ID (assuming Algeria has ID 1)
    const isAlgeria = countryName === 'Algeria' || countryName === 'Algérie' || countryId === 1;

    if (isAlgeria) {
      try {
        setLoadingWilayas(true);
        const wilayasData = await countriesService.getWilayas();
        setWilayas(wilayasData);
      } catch (error) {
      } finally {
        setLoadingWilayas(false);
      }
    } else {
      setWilayas([]);
    }
  };


  const handleDurationChange = (value: number, unit: string) => {
    setDurationValue(value);
    setDurationUnit(unit);
    const durationString = value > 0 ? `${value} ${unit}` : '';
    setFormData(prev => ({
      ...prev,
      duration: durationString
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, fileType: 'banner' | 'pdf') => {
    const file = e.target.files?.[0];
    const fileKey = fileType === 'banner' ? 'banner' : 'pdf';

    // Clear previous errors for this file type
    setFileErrors(prev => ({ ...prev, [fileKey]: '' }));

    if (file) {
      // Validate file size (10MB limit)
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes
      if (file.size > maxSize) {
        setFileErrors(prev => ({
          ...prev,
          [fileKey]: t('trainingCenter.createTraining.errors.fileUpload.fileTooLarge') + ' (' + t('trainingCenter.createTraining.errors.fileUpload.maxSize') + ')'
        }));
        e.target.value = ''; // Clear the input
        return;
      }

      // Validate file type
      if (fileType === 'banner') {
        const allowedImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
        if (!allowedImageTypes.includes(file.type)) {
          setFileErrors(prev => ({
            ...prev,
            [fileKey]: t('trainingCenter.createTraining.errors.fileUpload.imageRequired')
          }));
          e.target.value = ''; // Clear the input
          return;
        }
        setBannerFile(file);
        setFormData(prev => ({ ...prev, banner_image: file.name }));
      } else {
        if (file.type !== 'application/pdf') {
          setFileErrors(prev => ({
            ...prev,
            [fileKey]: t('trainingCenter.createTraining.errors.fileUpload.pdfRequired')
          }));
          e.target.value = ''; // Clear the input
          return;
        }
        setPdfFile(file);
        setFormData(prev => ({ ...prev, detailed_program_pdf: file.name }));
      }
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = t('trainingCenter.createTraining.errors.titleRequired');
    }

    if (!formData.subcategory_id) {
      newErrors.subcategory_id = t('trainingCenter.createTraining.errors.subcategoryRequired');
    }

    if (!formData.domaine_id || formData.domaine_id === null || formData.domaine_id === 0) {
      newErrors.domaine_id = t('trainingCenter.createTraining.errors.domainRequired');
    }

    // schedule_date is optional – no validation needed here

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const files = {
        banner: bannerFile || undefined,
        pdf: pdfFile || undefined,
      };

      // Always create the training (schedule_date is optional)
      const createdTraining = await trainingsService.createTraining(formData, files);

      // If the user filled in a date, auto-create a session with that date
      if (formData.schedule_date && createdTraining?.id) {
        try {
          await trainingsService.createSession(createdTraining.id, {
            session_date: formData.schedule_date,
          });
        } catch (sessionError) {
          // Don't block the user – they can add sessions manually
        }
      }

      // Show success message with approval notice
      const successMessage = formData.schedule_date
        ? t('trainingCenter.createTraining.messages.successWithSession', 'Formation créée avec succès. Une session a été programmée automatiquement.')
        : t('trainingCenter.createTraining.messages.success');
      alert(successMessage);
      navigate('/training-center/trainings');
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const err: any = error;
      const status = err?.response?.status;
      const errorData = err?.response?.data;

      // Handle structured error responses from backend
      if (errorData?.details) {
        const { message: errorMessage, allowedTypes, maxSize, maxFiles } = errorData.details;

        if (errorData.message === 'Invalid file type') {
          setFileErrors(prev => ({
            ...prev,
            banner: t('trainingCenter.createTraining.errors.fileUpload.invalidType'),
            pdf: t('trainingCenter.createTraining.errors.fileUpload.invalidType')
          }));
          return; // Don't show alert, errors are displayed in UI
        }

        if (errorData.message === 'File too large') {
          setFileErrors(prev => ({
            ...prev,
            banner: t('trainingCenter.createTraining.errors.fileUpload.fileTooLarge'),
            pdf: t('trainingCenter.createTraining.errors.fileUpload.fileTooLarge')
          }));
          return;
        }

        if (errorData.message === 'Too many files') {
          setFileErrors(prev => ({
            ...prev,
            banner: t('trainingCenter.createTraining.errors.fileUpload.tooManyFiles'),
            pdf: t('trainingCenter.createTraining.errors.fileUpload.tooManyFiles')
          }));
          return;
        }
      }

      // Handle legacy error messages
      let message = errorData?.message || t('trainingCenter.createTraining.errors.createFailed');

      // Handle file upload errors specifically
      if (message.includes('Only image') || message.includes('Only PDF')) {
        setFileErrors(prev => ({
          ...prev,
          banner: t('trainingCenter.createTraining.errors.fileUpload.imageRequired'),
          pdf: t('trainingCenter.createTraining.errors.fileUpload.pdfRequired')
        }));
        return;
      }

      if (status === 403) {
        const go = window.confirm(`${message}\n\n${t('trainingCenter.createTraining.messages.subscriptionPrompt')}`);
        if (go) {
          navigate('/training-center/subscriptions');
        }
      } else {
        alert(message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };



  // Show loading while checking permissions
  if (checkingPermission) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary mx-auto mb-4"></div>
          <p className="text-gray-600">{t('trainingCenter.createTraining.messages.checkingPermission')}</p>
        </div>
      </div>
    );
  }

  // Show subscription required message if can't create
  if (canCreate && !canCreate.canCreate) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white shadow">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-6">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{t('trainingCenter.createTraining.title')}</h1>
                <p className="text-gray-600">{t('trainingCenter.createTraining.subtitle')}</p>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => navigate('/training-center/trainings')}
                  className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700"
                >
                  {t('trainingCenter.createTraining.actions.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Subscription Required Message */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-brand-primary to-brand-secondary p-6">
              <div className="flex items-center space-x-3">
                <Crown className="h-8 w-8 text-white" />
                <div>
                  <h2 className="text-2xl font-bold text-white">{t('trainingCenter.createTraining.subscriptionRequired.title')}</h2>
                  <p className="text-white/90">{t('trainingCenter.createTraining.subscriptionRequired.subtitle')}</p>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="flex items-start space-x-4">
                <div className="p-3 bg-yellow-100 rounded-full">
                  <AlertCircle className="h-6 w-6 text-yellow-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {t('trainingCenter.createTraining.subscriptionRequired.limitReached')}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {canCreate.message || t('trainingCenter.createTraining.subscriptionRequired.message')}
                  </p>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-blue-900">{t('trainingCenter.createTraining.subscriptionRequired.freePlan.title')}</h4>
                        <p className="text-blue-700 text-sm">{t('trainingCenter.createTraining.subscriptionRequired.freePlan.description')}</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex space-x-4">
                    <button
                      onClick={() => navigate('/training-center/subscriptions')}
                      className="bg-brand-primary text-white px-6 py-3 rounded-lg hover:opacity-90 transition-colors flex items-center space-x-2"
                    >
                      <Crown className="h-5 w-5" />
                      <span>{t('trainingCenter.createTraining.subscriptionRequired.viewPlans')}</span>
                    </button>
                    <button
                      onClick={() => navigate('/training-center/trainings')}
                      className="bg-gray-100 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      {t('trainingCenter.createTraining.subscriptionRequired.backToTrainings')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t('trainingCenter.createTraining.title')}</h1>
              <p className="text-gray-600">{t('trainingCenter.createTraining.subtitle')}</p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => navigate('/training-center/trainings')}
                className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700"
              >
                {t('trainingCenter.createTraining.actions.cancel')}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                  {t('trainingCenter.createTraining.form.title')} *
                </label>
                <input
                  id="title"
                  name="title"
                  type="text"
                  required
                  value={formData.title}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${errors.title ? 'border-red-300' : 'border-gray-300'
                    }`}
                />
                {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
              </div>

              <div>
                <label htmlFor="reference" className="block text-sm font-medium text-gray-700">
                  {t('trainingCenter.createTraining.form.reference')}
                </label>
                <input
                  id="reference"
                  name="reference"
                  type="text"
                  value={formData.reference}
                  onChange={handleInputChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>
            </div>

            {/* Location */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="country_id" className="block text-sm font-medium text-gray-700">
                  {t('trainingCenter.createTraining.form.country')}
                </label>
                <select
                  id="country_id"
                  name="country_id"
                  value={formData.country_id || ''}
                  onChange={(e) => handleCountryChange(parseInt(e.target.value))}
                  disabled={loadingCountries}
                  className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${loadingCountries ? 'bg-gray-100' : 'border-gray-300'
                    }`}
                >
                  <option value="">
                    {loadingCountries ? t('common.loading') : t('trainingCenter.createTraining.form.selectCountry')}
                  </option>
                  {countries.map(country => (
                    <option key={country.id} value={country.id}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="city" className="block text-sm font-medium text-gray-700">
                  {(() => {
                    const selectedCountry = countries.find(c => c.id === formData.country_id);
                    const countryName = selectedCountry?.name || '';
                    const isAlgeria = countryName === 'Algeria' || countryName === 'Algérie' || formData.country_id === 1;
                    return isAlgeria ? t('common.wilaya') : t('trainingCenter.createTraining.form.city');
                  })()}
                </label>
                {(() => {
                  const selectedCountry = countries.find(c => c.id === formData.country_id);
                  const countryName = selectedCountry?.name || '';
                  const isAlgeria = countryName === 'Algeria' || countryName === 'Algérie' || formData.country_id === 1;

                  if (isAlgeria) {
                    return (
                      <select
                        id="wilaya_id"
                        name="wilaya_id"
                        value={formData.wilaya_id || ''}
                        onChange={(e) => {
                          const wilayaId = parseInt(e.target.value);
                          const selectedWilaya = wilayas.find(w => w.id === wilayaId);
                          setFormData(prev => ({
                            ...prev,
                            wilaya_id: wilayaId,
                            city: selectedWilaya?.name || ''
                          }));
                        }}
                        disabled={loadingWilayas}
                        className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${loadingWilayas ? 'bg-gray-100' : 'border-gray-300'
                          }`}
                      >
                        <option value="">
                          {loadingWilayas ? t('common.loading') : t('common.selectWilaya')}
                        </option>
                        {wilayas.map(wilaya => (
                          <option key={wilaya.id} value={wilaya.id}>
                            {wilaya.name}
                          </option>
                        ))}
                      </select>
                    );
                  } else {
                    return (
                      <input
                        id="city"
                        name="city"
                        type="text"
                        value={formData.city}
                        onChange={handleInputChange}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder={t('trainingCenter.createTraining.form.cityPlaceholder')}
                      />
                    );
                  }
                })()}
              </div>
            </div>

            {/* Hierarchical Selection: Subcategory → Field */}
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900">{t('trainingCenter.createTraining.form.classification')} *</h3>

              {/* Subcategory Selection */}
              <div>
                <label htmlFor="subcategory_id" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('trainingCenter.createTraining.form.subcategory')} *
                </label>
                {loadingSubcategories ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                    <span className="ml-2 text-sm text-gray-500">{t('common.loadingSubcategories')}</span>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {subcategories.map((subcategory) => (
                      <label
                        key={subcategory.id}
                        className={`flex items-start p-3 rounded-lg border-2 cursor-pointer transition-all ${formData.subcategory_id === subcategory.id
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-gray-200 hover:border-gray-300'
                          }`}
                      >
                        <input
                          type="radio"
                          name="subcategory"
                          value={subcategory.id}
                          checked={formData.subcategory_id === subcategory.id}
                          onChange={() => setFormData(prev => ({ ...prev, subcategory_id: subcategory.id, domaine_id: 0 }))}
                          className="sr-only"
                        />
                        <div className={`w-4 h-4 rounded-full border-2 mr-3 mt-0.5 flex items-center justify-center ${formData.subcategory_id === subcategory.id
                          ? 'border-indigo-500 bg-indigo-500'
                          : 'border-gray-300'
                          }`}>
                          {formData.subcategory_id === subcategory.id && (
                            <div className="w-2 h-2 bg-white rounded-full"></div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-700">
                            {getLocalizedName(subcategory)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {subcategory.name_french || subcategory.name_english}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
                {errors.subcategory_id && (
                  <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    {errors.subcategory_id}
                  </p>
                )}
              </div>

              {/* Field Selection - Only show if subcategory is selected */}
              {formData.subcategory_id && (
                <div>
                  <label htmlFor="domaine_id" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('trainingCenter.createTraining.form.domain')} *
                  </label>
                  {loadingFields ? (
                    <div className="flex items-center justify-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                      <span className="ml-2 text-sm text-gray-500">{t('common.loadingFields')}</span>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {availableFields.map((field) => (
                        <label
                          key={field.id}
                          className={`flex items-start p-3 rounded-lg border-2 cursor-pointer transition-all ${formData.domaine_id === field.id
                            ? 'border-indigo-500 bg-indigo-50'
                            : 'border-gray-200 hover:border-gray-300'
                            }`}
                        >
                          <input
                            type="radio"
                            name="field"
                            value={field.id}
                            checked={formData.domaine_id === field.id}
                            onChange={() => setFormData(prev => ({ ...prev, domaine_id: field.id }))}
                            className="sr-only"
                          />
                          <div className={`w-4 h-4 rounded-full border-2 mr-3 mt-0.5 flex items-center justify-center ${formData.domaine_id === field.id
                            ? 'border-indigo-500 bg-indigo-500'
                            : 'border-gray-300'
                            }`}>
                            {formData.domaine_id === field.id && (
                              <div className="w-2 h-2 bg-white rounded-full"></div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-700">
                              {getLocalizedName(field)}
                            </div>
                            <div className="text-xs text-gray-500">
                              {field.name_french || field.name_english}
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                  {errors.domaine_id && (
                    <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="h-4 w-4" />
                      {errors.domaine_id}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Schedule */}
            <div>
              <label htmlFor="schedule_date" className="block text-sm font-medium text-gray-700">
                {t('trainingCenter.createTraining.form.scheduleDate')}
                <span className="ml-1 text-xs font-normal text-gray-400">({t('common.optional', 'optionnel')})</span>
              </label>
              <input
                id="schedule_date"
                name="schedule_date"
                type="date"
                min={new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                value={formData.schedule_date}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
              <p className="mt-1 text-xs text-blue-600">
                💡 {t(
                  'trainingCenter.createTraining.form.scheduleDateOptionalHint',
                  'Si vous renseignez une date, une session sera créée automatiquement. Sinon, vous pouvez ajouter des sessions plus tard.'
                )}
              </p>
            </div>

            {/* Training Details */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label htmlFor="format" className="block text-sm font-medium text-gray-700">
                  {t('trainingCenter.createTraining.form.format')}
                </label>
                <select
                  id="format"
                  name="format"
                  value={formData.format}
                  onChange={handleInputChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                >
                  <option value="">{t('trainingCenter.createTraining.form.selectFormat')}</option>
                  <option value="En présentiel">{t('formations.format.presentiel')}</option>
                  <option value="En ligne">{t('formations.format.online')}</option>
                  <option value="Hybride">{t('formations.format.hybrid')}</option>
                </select>
              </div>

              <div>
                <label htmlFor="level" className="block text-sm font-medium text-gray-700">
                  {t('trainingCenter.createTraining.form.level')}
                </label>
                <select
                  id="level"
                  name="level"
                  value={formData.level}
                  onChange={handleInputChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  disabled={loadingLevels}
                >
                  <option value="">{t('trainingCenter.createTraining.form.selectLevel')}</option>
                  {levels.map((level) => (
                    <option key={level.id} value={level.name_french}>
                      {level.name_french}
                    </option>
                  ))}
                </select>
                {loadingLevels && (
                  <p className="mt-1 text-sm text-gray-500">{t('common.loadingLevels')}</p>
                )}
              </div>

              <div>
                <label htmlFor="duration" className="block text-sm font-medium text-gray-700">
                  {t('trainingCenter.createTraining.form.duration')}
                </label>
                <div className="mt-1 flex gap-2">
                  <input
                    id="duration"
                    name="duration"
                    type="number"
                    min="0"
                    value={durationValue}
                    onChange={(e) => handleDurationChange(Number(e.target.value), durationUnit)}
                    placeholder="0"
                    className="block w-24 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                  <select
                    value={durationUnit}
                    onChange={(e) => handleDurationChange(durationValue, e.target.value)}
                    className="block px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  >
                    <option value="heures">{t('formations.duration.hours')}</option>
                    <option value="jours">{t('formations.duration.days')}</option>
                    <option value="semaines">{t('formations.duration.weeks')}</option>
                    <option value="mois">{t('formations.duration.months')}</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Pricing and Language */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="pricing_dzd" className="block text-sm font-medium text-gray-700">
                  {t('trainingCenter.createTraining.form.pricing')}
                </label>
                <input
                  id="pricing_dzd"
                  name="pricing_dzd"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.pricing_dzd || 0}
                  onChange={handleInputChange}
                  placeholder={t('trainingCenter.createTraining.form.pricingPlaceholder')}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>

              <div>
                <label htmlFor="language" className="block text-sm font-medium text-gray-700">
                  {t('trainingCenter.createTraining.form.language')}
                </label>
                <select
                  id="language"
                  name="language"
                  value={formData.language}
                  onChange={handleInputChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                >
                  <option value="">{t('trainingCenter.createTraining.form.selectLanguage')}</option>
                  <option value="Arabe">{t('common.languages.arabic')}</option>
                  <option value="Anglais">{t('common.languages.english')}</option>
                  <option value="Français">{t('common.languages.french')}</option>
                  <option value="Arabe/Anglais">{t('common.languages.arabicEnglish')}</option>
                  <option value="Arabe/Francais">{t('common.languages.arabicFrench')}</option>
                  <option value="Anglais/Francais">{t('common.languages.englishFrench')}</option>
                </select>
              </div>
            </div>

            {/* Description */}
            <div>
              <label htmlFor="full_description" className="block text-sm font-medium text-gray-700">
                {t('trainingCenter.createTraining.form.fullDescription')}
              </label>
              <textarea
                id="full_description"
                name="full_description"
                rows={4}
                value={formData.full_description}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder={t('trainingCenter.createTraining.form.fullDescriptionPlaceholder')}
              />
            </div>

            {/* Learning Objectives */}
            <div>
              <label htmlFor="learning_objectives" className="block text-sm font-medium text-gray-700">
                {t('trainingCenter.createTraining.form.learningObjectives')}
              </label>
              <textarea
                id="learning_objectives"
                name="learning_objectives"
                rows={3}
                value={formData.learning_objectives}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder={t('trainingCenter.createTraining.form.learningObjectivesPlaceholder')}
              />
            </div>

            {/* Skills and Target Audience */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="skills_gained" className="block text-sm font-medium text-gray-700">
                  {t('trainingCenter.createTraining.form.skillsGained')}
                </label>
                <textarea
                  id="skills_gained"
                  name="skills_gained"
                  rows={3}
                  value={formData.skills_gained}
                  onChange={handleInputChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder={t('trainingCenter.createTraining.form.skillsGainedPlaceholder')}
                />
              </div>

              <div>
                <label htmlFor="target_audience" className="block text-sm font-medium text-gray-700">
                  {t('trainingCenter.createTraining.form.targetAudience')}
                </label>
                <textarea
                  id="target_audience"
                  name="target_audience"
                  rows={3}
                  value={formData.target_audience}
                  onChange={handleInputChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder={t('trainingCenter.createTraining.form.targetAudiencePlaceholder')}
                />
              </div>
            </div>

            {/* Trainer */}
            <div>
              <label htmlFor="trainer" className="block text-sm font-medium text-gray-700">
                {t('trainingCenter.createTraining.form.trainer')}
              </label>
              <input
                id="trainer"
                name="trainer"
                type="text"
                value={formData.trainer}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder={t('trainingCenter.createTraining.form.trainerPlaceholder')}
              />
            </div>

            {/* File Uploads */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="banner_file" className="block text-sm font-medium text-gray-700">
                  {t('trainingCenter.createTraining.form.bannerImage')}
                </label>
                <div className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md ${fileErrors.banner ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}>
                  <div className="space-y-1 text-center">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 48 48"
                      aria-hidden="true"
                    >
                      <path
                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div className="flex text-sm text-gray-600">
                      <label
                        htmlFor="banner_file"
                        className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500"
                      >
                        <span>{t('common.uploadFile')}</span>
                        <input
                          id="banner_file"
                          name="banner_file"
                          type="file"
                          accept="image/*"
                          className="sr-only"
                          onChange={(e) => handleFileChange(e, 'banner')}
                        />
                      </label>
                      <p className="pl-1">{t('common.orDragDrop')}</p>
                    </div>
                    <p className="text-xs text-gray-500">{t('common.fileSizeLimit')}</p>
                    {bannerFile && (
                      <p className="text-sm text-green-600">{t('common.selected')}: {bannerFile.name}</p>
                    )}
                    {fileErrors.banner && (
                      <p className="text-sm text-red-600 flex items-center gap-1 mt-2">
                        <AlertCircle className="h-4 w-4" />
                        {fileErrors.banner}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="pdf_file" className="block text-sm font-medium text-gray-700">
                  {t('trainingCenter.createTraining.form.detailedProgramPdf')}
                </label>
                <div className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md ${fileErrors.pdf ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}>
                  <div className="space-y-1 text-center">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 48 48"
                      aria-hidden="true"
                    >
                      <path
                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div className="flex text-sm text-gray-600">
                      <label
                        htmlFor="pdf_file"
                        className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500"
                      >
                        <span>{t('common.uploadFile')}</span>
                        <input
                          id="pdf_file"
                          name="pdf_file"
                          type="file"
                          accept=".pdf"
                          className="sr-only"
                          onChange={(e) => handleFileChange(e, 'pdf')}
                        />
                      </label>
                      <p className="pl-1">{t('common.orDragDrop')}</p>
                    </div>
                    <p className="text-xs text-gray-500">{t('common.pdfFileSizeLimit')}</p>
                    {pdfFile && (
                      <p className="text-sm text-green-600">{t('common.selected')}: {pdfFile.name}</p>
                    )}
                    {fileErrors.pdf && (
                      <p className="text-sm text-red-600 flex items-center gap-1 mt-2">
                        <AlertCircle className="h-4 w-4" />
                        {fileErrors.pdf}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end space-x-3 pt-6">
              <button
                type="button"
                onClick={() => navigate('/training-center/trainings')}
                className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700"
              >
                {t('trainingCenter.createTraining.actions.cancel')}
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 disabled:opacity-50"
              >
                {isSubmitting ? t('trainingCenter.createTraining.actions.creating') : t('trainingCenter.createTraining.actions.create')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateTrainingPage; 